import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled, CircleRegular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';

import { SectionCard } from '../components/shared';
import { getChangeV2ExecutionPath } from '../helpers';
import { CHANGE_STATUS } from '../models';
import { useChangeV2Store } from '../state/ChangeV2Store';
import { getReleaseReadiness } from '../utils';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  heading: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  steps: { display: 'flex', flexDirection: 'column' },
  step: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
  },
  done: { color: tokens.colorStatusSuccessForeground1 },
  pending: { color: tokens.colorNeutralForeground3 },
  stepText: { display: 'flex', flexDirection: 'column', gap: '2px' },
});

type TCompletionStepProps = {
  title: string;
  description: string;
  done: boolean;
  actionLabel: string;
  disabled?: boolean;
  onAction: () => void;
};

const CompletionStep = ({ title, description, done, actionLabel, disabled, onAction }: TCompletionStepProps) => {
  const classes = useStyles();
  return (
    <div className={classes.step}>
      {done ? (
        <CheckmarkCircleFilled className={classes.done} aria-label="Complete" />
      ) : (
        <CircleRegular className={classes.pending} aria-label="Pending" />
      )}
      <div className={classes.stepText}>
        <Body1Strong>{title}</Body1Strong>
        <Caption1 className={classes.muted}>{description}</Caption1>
      </div>
      <Button appearance={done ? 'subtle' : 'secondary'} disabled={done || disabled} onClick={onAction}>
        {done ? 'Done' : actionLabel}
      </Button>
    </div>
  );
};

const CompletionPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { changeId } = useParams();
  const { show } = useContext(ToastContext);
  const { getChange, checkReleaseReadiness, approveRelease, completeEffectivenessReview, closeChange } =
    useChangeV2Store();

  const change = changeId ? getChange(changeId) : undefined;
  if (!change) return null;

  const completion = change.completion;
  const readiness = getReleaseReadiness(change);

  if (!completion) {
    return (
      <MessageBar intent={readiness.eligible ? 'info' : 'warning'}>
        <MessageBarBody>
          <MessageBarTitle>
            {readiness.eligible ? 'Completion has not started' : 'Execution is not ready for completion'}
          </MessageBarTitle>
          {readiness.eligible ? 'Start Completion from Execution.' : readiness.blockers.join('. ')}{' '}
          <Button appearance="transparent" size="small" onClick={() => navigate(getChangeV2ExecutionPath(change.id))}>
            Open execution
          </Button>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const readinessDone = Boolean(completion.releaseReadinessCheckedAt);
  const releaseDone = Boolean(completion.releaseApprovedAt);
  const effectivenessDone = Boolean(completion.effectivenessReviewedAt);
  const closed = change.status === CHANGE_STATUS.CLOSED;
  const canClose = releaseDone && (!completion.effectivenessReviewRequired || effectivenessDone);

  return (
    <div className={classes.root}>
      <div className={classes.heading}>
        <Subtitle1>Completion</Subtitle1>
        <Caption1 className={classes.muted}>
          Release readiness, release approval{completion.effectivenessReviewRequired ? ', effectiveness review' : ''}
          {' and '}Change closure
        </Caption1>
      </div>

      {closed ? (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Change closed</MessageBarTitle>
            All required Completion work is finished. Closed is the current status.
          </MessageBarBody>
        </MessageBar>
      ) : !readiness.eligible ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Release readiness is blocked</MessageBarTitle>
            {readiness.blockers.join('. ')}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <SectionCard title="Completion checklist" description="Complete these actions in order.">
        <div className={classes.steps}>
          <CompletionStep
            title="Check release readiness"
            description="Confirm required tasks and external decisions are complete."
            done={readinessDone}
            actionLabel="Check readiness"
            disabled={!readiness.eligible}
            onAction={() => {
              checkReleaseReadiness(change.id);
              show('success', 'Release readiness checked.');
            }}
          />
          <CompletionStep
            title="Approve release"
            description="Record the final release decision."
            done={releaseDone}
            actionLabel="Approve release"
            disabled={!readinessDone}
            onAction={() => {
              approveRelease(change.id);
              show('success', 'Release approved.');
            }}
          />
          {completion.effectivenessReviewRequired ? (
            <CompletionStep
              title="Perform effectiveness review"
              description="Confirm the implemented change achieved the intended result."
              done={effectivenessDone}
              actionLabel="Complete review"
              disabled={!releaseDone}
              onAction={() => {
                completeEffectivenessReview(change.id);
                show('success', 'Effectiveness review completed.');
              }}
            />
          ) : null}
          <CompletionStep
            title="Close the Change"
            description="Set the Change status to Closed."
            done={closed}
            actionLabel="Close Change"
            disabled={!canClose}
            onAction={() => {
              closeChange(change.id);
              show('success', 'Change closed.');
            }}
          />
        </div>
      </SectionCard>

      <Body1 className={classes.muted}>
        Closed is a status within Completion; it is not an additional lifecycle phase.
      </Body1>
    </div>
  );
};

export default CompletionPage;
